import React, { useState, useEffect } from 'react';
import { formatDateTimeString, formatDateString } from '../lib/utils';
import { ServiceProvider, serviceProviderService, getProviderServiceOfferings, getProviderServiceCount } from '../services/serviceProviderService';
import { StatusUpdateModal } from './StatusUpdateModal';

// Use ServiceProvider interface from the service
type Mechanic = ServiceProvider;

interface ServiceProviderDetailViewProps {
  mechanic: Mechanic;
  onBack: () => void;
  onRefresh?: () => void;
}

export const ServiceProviderDetailView: React.FC<ServiceProviderDetailViewProps> = ({ mechanic, onBack, onRefresh }) => {
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isSendingNotification, setIsSendingNotification] = useState(false);
  const [notificationError, setNotificationError] = useState<string | null>(null);
  const [fullMechanic, setFullMechanic] = useState<Mechanic>(mechanic);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const serviceOfferings = getProviderServiceOfferings(fullMechanic);
  const servicesOffered = getProviderServiceCount(fullMechanic);

  const handleStatusUpdate = async (mobileNumber: string, status: 'enabled' | 'disabled' | 'blocked' | 'unblocked', reason?: string) => {
    setIsUpdatingStatus(true);
    try {
      // Status update logging removed for security
      const response = await serviceProviderService.updateServiceProviderStatus(mobileNumber, status, reason);
      // Status update success logging removed for security
      
      setSuccessMessage(response.message || `Service provider ${status} successfully`);
      setStatusModalOpen(false);
      
      // Refresh the data if onRefresh callback is provided
      if (onRefresh) {
        // Refresh callback logging removed for security
        onRefresh();
      } else {
        console.warn('⚠️ No onRefresh callback provided');
      }
      
      // Also refresh the full profile
      if (mechanic.contactNumber) {
        try {
          const profileResponse = await serviceProviderService.getServiceProviderProfile(mechanic.contactNumber);
          if (profileResponse.success && profileResponse.data?.serviceProvider) {
            const profile = profileResponse.data.serviceProvider;
            const device = profile.device;

            // Map the profile to ServiceProvider format including plan details
            const serviceProvider: ServiceProvider = {
              id: profile.id,
              fullName: profile.fullName,
              contactNumber: profile.contactNumber,
              address: profile.businessAddress || '',
              businessAddress: profile.businessAddress,
              formattedAddress: profile.formattedAddress,
              registeredOn: profile.registeredOn,
              updatedAt: profile.updatedAt,
              servicesOffered: getProviderServiceCount(profile),
              status: profile.status,
              isActive: profile.isActive,
              isAvailable: profile.isAvailable,
              overallRating: profile.overallRating,
              ratingCount: profile.ratingCount,
              serviceProviderType: profile.serviceProviderType,
              categories: profile.categories,
              serviceOfferings: profile.serviceOfferings,
              email: profile.email,
              tradeName: profile.tradeName,
              specialization: profile.specialization,
              profileStatus: profile.profileStatus,
              paymentStatus: profile.paymentStatus,
              documents: profile.documents,
              serviceType: profile.serviceProviderType?.name,
              // Plan details from profile
              planId: profile.planId,
              plan: profile.plan,
              planValidityUntil: profile.planValidityUntil,
              isPlanExpired: profile.isPlanExpired,
              paymentDetails: profile.paymentDetails,
              // Map device-related fields from profile API response
              deviceInfo: device,
              // Legacy device fields for backward compatibility
              fcmToken: device?.fcmToken,
              deviceType: device?.deviceType,
              deviceId: device?.deviceId,
              deviceModel: device?.deviceModel,
              osVersion: device?.osVersion,
            };

            setFullMechanic(serviceProvider);
          }
        } catch (error) {
          console.error('Error refreshing profile:', error);
        }
      }
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (error) {
      console.error('❌ Status update failed:', error);
      throw error; // Re-throw to let the modal handle the error display
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleCloseStatusModal = () => {
    setStatusModalOpen(false);
  };

  // Fetch full profile with plan details when component mounts or mechanic changes
  useEffect(() => {
    const fetchFullProfile = async () => {
      if (!mechanic.contactNumber) return;
      
      // Always fetch the full profile to ensure we have the latest plan data
      setIsLoadingProfile(true);
      try {
        const profileResponse = await serviceProviderService.getServiceProviderProfile(mechanic.contactNumber);
        if (profileResponse.success && profileResponse.data?.serviceProvider) {
          const profile = profileResponse.data.serviceProvider;
          const device = profile.device;

          // Map the profile to ServiceProvider format including plan details
          const serviceProvider: ServiceProvider = {
            id: profile.id,
            fullName: profile.fullName,
            contactNumber: profile.contactNumber,
            address: profile.businessAddress || '',
            businessAddress: profile.businessAddress,
            formattedAddress: profile.formattedAddress,
            registeredOn: profile.registeredOn,
            updatedAt: profile.updatedAt,
            servicesOffered: getProviderServiceCount(profile),
            status: profile.status,
            isActive: profile.isActive,
            isAvailable: profile.isAvailable,
            overallRating: profile.overallRating,
            ratingCount: profile.ratingCount,
            serviceProviderType: profile.serviceProviderType,
            categories: profile.categories,
            serviceOfferings: profile.serviceOfferings,
            email: profile.email,
            tradeName: profile.tradeName,
            specialization: profile.specialization,
            profileStatus: profile.profileStatus,
            paymentStatus: profile.paymentStatus,
            documents: profile.documents,
            serviceType: profile.serviceProviderType?.name,
            // Plan details from profile - use the same mapping as RegisteredMechanics
            planId: profile.planId,
            plan: profile.plan,
            planValidityUntil: profile.planValidityUntil,
            isPlanExpired: profile.isPlanExpired,
            paymentDetails: profile.paymentDetails,
            // Map device-related fields from profile API response
            deviceInfo: device,
            // Legacy device fields for backward compatibility
            fcmToken: device?.fcmToken,
            deviceType: device?.deviceType,
            deviceId: device?.deviceId,
            deviceModel: device?.deviceModel,
            osVersion: device?.osVersion,
          };

          setFullMechanic(serviceProvider);
        } else {
          setFullMechanic(mechanic);
        }
      } catch (error) {
        console.error('❌ Error fetching full profile:', error);
        // Fallback to the mechanic prop if profile fetch fails
        setFullMechanic(mechanic);
      } finally {
        setIsLoadingProfile(false);
      }
    };

    fetchFullProfile();
  }, [mechanic.contactNumber]);

  const handleTestNotification = async () => {
    // Get device info from fullMechanic
    const deviceInfo = fullMechanic.deviceInfo;
    const fcmToken = deviceInfo?.fcmToken || fullMechanic.fcmToken;
    const deviceType = deviceInfo?.deviceType || fullMechanic.deviceType;

    if (!fcmToken || fcmToken.trim() === '') {
      setNotificationError('FCM token is not available for this service provider');
      setTimeout(() => setNotificationError(null), 5000);
      return;
    }

    setIsSendingNotification(true);
    setNotificationError(null);

    try {
      // Validate FCM token format (should be a non-empty string)
      const trimmedToken = fcmToken.trim();
      if (!trimmedToken || trimmedToken.length < 10) {
        setNotificationError('FCM token appears to be invalid or too short');
        setTimeout(() => setNotificationError(null), 5000);
        setIsSendingNotification(false);
        return;
      }

      // Build request payload
      const requestPayload: {
        fcmToken: string;
        deviceType?: 'ios' | 'android';
        title?: string;
        body?: string;
        data?: Record<string, string>;
      } = {
        fcmToken: trimmedToken,
      };

      // Only add deviceType if it's valid
      if (deviceType && (deviceType === 'ios' || deviceType === 'android')) {
        requestPayload.deviceType = deviceType;
      }

      // Add optional fields
      requestPayload.title = 'Test Notification';
      requestPayload.body = 'This is a test notification from the mechanic admin console';
      requestPayload.data = {
        testId: Date.now().toString(),
        source: 'admin-console'
      };

      const response = await serviceProviderService.sendTestNotification(requestPayload);

      if (response.success) {
        setSuccessMessage(response.message || 'Test notification sent successfully');
        setTimeout(() => setSuccessMessage(null), 5000);
      } else {
        setNotificationError(response.error || response.message || 'Failed to send test notification');
        setTimeout(() => setNotificationError(null), 5000);
      }
    } catch (error: unknown) {
      // Extract detailed error message from API response
      let errorMessage = 'An unexpected error occurred';
      
      if (error instanceof Error) {
        errorMessage = error.message;
        
        // Check if there's additional error data from the API
        const errorWithData = error as Error & { errorData?: { error?: string; message?: string; details?: string } };
        if (errorWithData.errorData) {
          const apiError = errorWithData.errorData;
          if (apiError.details) {
            errorMessage = `${apiError.error || apiError.message || 'Error'}: ${apiError.details}`;
          } else if (apiError.error) {
            errorMessage = apiError.error;
          } else if (apiError.message) {
            errorMessage = apiError.message;
          }
        }
      }
      
      console.error('Test notification error:', error);
      setNotificationError(errorMessage);
      setTimeout(() => setNotificationError(null), 5000);
    } finally {
      setIsSendingNotification(false);
    }
  };

  const getStatusBadgeClass = (status: Mechanic['status']) => {
    // Handle both numeric and string status values
    const statusValue = typeof status === 'number' ? status : status;
    
    switch (statusValue) {
      case 1:
      case 'enabled':
        return 'bg-green-100 text-green-800 border-green-200';
      case 0:
      case 'disabled':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'blocked':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: Mechanic['status']) => {
    // Handle both numeric and string status values
    const statusValue = typeof status === 'number' ? status : status;
    
    switch (statusValue) {
      case 1:
      case 'enabled':
        return (
          <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 0:
      case 'disabled':
        return (
          <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        );
      case 'blocked':
        return (
          <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636M5.636 18.364l12.728-12.728" />
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex">
            <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="ml-3">
              <p className="text-sm text-green-800">{successMessage}</p>
            </div>
          </div>
        </div>
      )}

      {/* Notification Error Message */}
      {notificationError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="ml-3">
              <p className="text-sm text-red-800">{notificationError}</p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-6 flex items-start justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={onBack}
            className="inline-flex items-center justify-center w-8 h-8 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Service Provider Details</h1>
            <p className="text-gray-600 mt-1">View detailed information about {fullMechanic.fullName || 'this service provider'}</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusBadgeClass(fullMechanic.status)}`}>
            {getStatusIcon(fullMechanic.status)}
            <span className="ml-2">
              {typeof fullMechanic.status === 'number' 
                ? (fullMechanic.status === 1 ? 'Enabled' : 'Disabled')
                : fullMechanic.status && typeof fullMechanic.status === 'string'
                ? fullMechanic.status.charAt(0).toUpperCase() + fullMechanic.status.slice(1)
                : 'Unknown'
              }
            </span>
          </span>
        </div>
      </div>

      {/* Main Content */}
      <div className="space-y-6">
          {/* Basic Information and Payment & Membership Information Cards in one row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Basic Information Card */}
            <div className="bg-white rounded-lg shadow border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900 flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg mr-3">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                Basic Information
              </h2>
            </div>
            <div className="px-6 py-4">
              <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Full Name</dt>
                  <dd className="mt-1 text-sm text-gray-900">{fullMechanic.fullName || 'Not available'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Contact Number</dt>
                  <dd className="mt-1 text-sm text-gray-900">{fullMechanic.contactNumber || 'Not available'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Registration Date</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {fullMechanic.registeredOn 
                      ? (fullMechanic.registeredOn.includes(',') 
                          ? fullMechanic.registeredOn 
                          : formatDateTimeString(fullMechanic.registeredOn))
                      : 'Not available'}
                  </dd>
                </div>
                <div className="sm:col-span-2">
                  <dt className="text-sm font-medium text-gray-500">Address</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {fullMechanic.formattedAddress || fullMechanic.businessAddress || fullMechanic.address ? (
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullMechanic.formattedAddress || fullMechanic.businessAddress || fullMechanic.address || '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer inline-flex items-center"
                      >
                        {fullMechanic.formattedAddress || fullMechanic.businessAddress || fullMechanic.address}
                        <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                    ) : (
                      'Not available'
                    )}
                  </dd>
                </div>
                {(fullMechanic.overallRating !== undefined || fullMechanic.ratingCount !== undefined) && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Rating</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {fullMechanic.overallRating !== undefined
                        ? `${fullMechanic.overallRating.toFixed(1)} / 5${fullMechanic.ratingCount !== undefined ? ` (${fullMechanic.ratingCount} ratings)` : ''}`
                        : 'Not available'}
                    </dd>
                  </div>
                )}
                {fullMechanic.isAvailable !== undefined && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Mobile Availability</dt>
                    <dd className={`mt-1 text-sm font-semibold ${fullMechanic.isAvailable ? 'text-green-700' : 'text-gray-600'}`}>
                      {fullMechanic.isAvailable ? 'Available' : 'Unavailable'}
                    </dd>
                  </div>
                )}
                {fullMechanic.updatedAt && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Last Updated</dt>
                    <dd className="mt-1 text-sm text-gray-900">{formatDateTimeString(fullMechanic.updatedAt)}</dd>
                  </div>
                )}
              </dl>
            </div>
          </div>

          {/* Plan Information Card */}
          {(fullMechanic.plan || fullMechanic.planId || fullMechanic.paymentDetails) && (
            <div className="bg-white rounded-lg shadow border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900 flex items-center">
                  <div className="p-2 bg-green-100 rounded-lg mr-3">
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-6 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                  </div>
                  {fullMechanic.plan ? 'Plan Information' : 'Payment & Membership Information'}
                </h2>
              </div>
              <div className="px-6 py-4">
                <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                  {/* Plan Information (if available) */}
                  {fullMechanic.plan && (
                    <>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Plan Name</dt>
                        <dd className="mt-1 text-sm font-semibold text-gray-900">{fullMechanic.plan.planName || 'N/A'}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Plan Amount</dt>
                        <dd className="mt-1 text-sm font-semibold text-gray-900">
                          {fullMechanic.plan.currency || 'USD'} {fullMechanic.plan.amount?.toFixed(2) || '0.00'}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Validity Period</dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {fullMechanic.plan.validity === 1 ? '1 month' : `${fullMechanic.plan.validity || 0} months`}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Plan Status</dt>
                        <dd className="mt-1">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              fullMechanic.plan.isActive
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {fullMechanic.plan.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </dd>
                      </div>
                      {fullMechanic.plan.description && (
                        <div className="sm:col-span-2">
                          <dt className="text-sm font-medium text-gray-500">Description</dt>
                          <dd className="mt-1 text-sm text-gray-900">{fullMechanic.plan.description}</dd>
                        </div>
                      )}
                      {fullMechanic.plan.features && fullMechanic.plan.features.length > 0 && (
                        <div className="sm:col-span-2">
                          <dt className="text-sm font-medium text-gray-500">Features</dt>
                          <dd className="mt-1 text-sm text-gray-900">
                            <ul className="list-disc pl-5">
                              {fullMechanic.plan.features.map((feature, index) => <li key={`${feature}-${index}`}>{feature}</li>)}
                            </ul>
                          </dd>
                        </div>
                      )}
                    </>
                  )}
                  
                  {/* Plan Validity Information */}
                  {fullMechanic.planValidityUntil && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Valid Until</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {formatDateTimeString(fullMechanic.planValidityUntil)}
                      </dd>
                    </div>
                  )}
                  
                  {fullMechanic.isPlanExpired !== undefined && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Expiry Status</dt>
                      <dd className="mt-1">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            fullMechanic.isPlanExpired
                              ? 'bg-red-100 text-red-800'
                              : 'bg-green-100 text-green-800'
                          }`}
                        >
                          {fullMechanic.isPlanExpired ? 'Expired' : 'Valid'}
                        </span>
                      </dd>
                    </div>
                  )}
                  
                  {/* Payment Details (always show if available) */}
                  {fullMechanic.paymentDetails && (
                    <>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Payment Amount</dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {fullMechanic.paymentDetails.currency || 'USD'} {(fullMechanic.paymentDetails.amount || 0).toFixed(2)}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Payment Date</dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {formatDateTimeString(fullMechanic.paymentDetails.paymentDate)}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Payment Method</dt>
                        <dd className="mt-1 text-sm text-gray-900 capitalize">
                          {fullMechanic.paymentDetails.paymentMethod}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Membership Expiry</dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {formatDateTimeString(fullMechanic.paymentDetails.membershipExpiry)}
                        </dd>
                      </div>
                    </>
                  )}
                  
                  {/* Show message if no plan but has payment details */}
                  {!fullMechanic.plan && !fullMechanic.planId && fullMechanic.paymentDetails && (
                    <div className="sm:col-span-2">
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <p className="text-sm text-yellow-800">
                          <span className="font-medium">Note:</span> This service provider has payment details but no active plan assigned.
                        </p>
                      </div>
                    </div>
                  )}
                </dl>
              </div>
            </div>
          )}
          </div>

          {/* Services Information Card */}
          <div className="bg-white rounded-lg shadow border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900 flex items-center">
                <div className="p-2 bg-purple-100 rounded-lg mr-3">
                  <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                Services Information
              </h2>
            </div>
            <div className="px-6 py-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Services Offered</h3>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {servicesOffered.toString().padStart(2, '0')}
                  </p>
                </div>
                <div className="p-3 bg-blue-100 rounded-full">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">Service types and subcategories</h3>
                {serviceOfferings.length > 0 ? (
                  <ul aria-label="Selected services" className="divide-y divide-gray-200">
                    {serviceOfferings.map((offering, index) => {
                      const typeName = offering.serviceProviderType.name || offering.serviceProviderType.tag || 'Not specified';
                      return (
                        <li
                          key={offering.serviceProviderType._id || offering.serviceProviderType.tag || index}
                          aria-label={typeName}
                          className="py-4 first:pt-0 last:pb-0 min-w-0"
                        >
                          <h4 className="text-sm font-semibold text-gray-900 break-words mb-2">{typeName}</h4>
                          <div className="flex flex-wrap gap-2">
                            {offering.categories.length > 0 ? offering.categories.map((category) => (
                              <span
                                key={category._id || category.tag}
                                className="inline-flex max-w-full items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200 break-words"
                                title={category.description}
                              >
                                {category.name || category.tag}
                              </span>
                            )) : (
                              <span className="text-sm text-gray-500">No subcategories selected</span>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-500">No services selected</p>
                )}
              </div>
            </div>
          </div>

          {/* Documents and Device Details in one row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Documents Information Card */}
          <div className="bg-white rounded-lg shadow border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900 flex items-center">
                <div className="p-2 bg-orange-100 rounded-lg mr-3">
                  <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                Documents
              </h2>
            </div>
            <div className="px-6 py-4">
              {fullMechanic.documents ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {/* Profile Photo */}
                  {fullMechanic.documents.profilePhoto && (
                    <div className="text-center">
                      <h3 className="text-sm font-medium text-gray-500 mb-3">Profile Photo</h3>
                      <div className="relative">
                        <img
                          src={fullMechanic.documents.profilePhoto.url}
                          alt="Profile"
                          className="w-full h-32 object-cover rounded-lg border border-gray-200"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTI4IiBoZWlnaHQ9IjEyOCIgdmlld0JveD0iMCAwIDEyOCAxMjgiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMjgiIGhlaWdodD0iMTI4IiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik02NCA0MEM3My45NDExIDQwIDgyIDQ4LjA1ODkgODIgNThDODIgNjcuOTQxMSA3My45NDExIDc2IDY0IDc2QzU0LjA1ODkgNzYgNDYgNjcuOTQxMSA0NiA1OEM0NiA0OC4wNTg5IDU0LjA1ODkgNDAgNjQgNDBaIiBmaWxsPSIjOUNBM0FGIi8+CjxwYXRoIGQ9Ik0zNiA5NkMzNiA4NS4wNTg5IDQ0LjA1ODkgNzcgNTUgNzdINzNDODMuOTQxMSA3NyA5MiA4NS4wNTg5IDkyIDk2VjEwNEgzNlY5NloiIGZpbGw9IiM5Q0EzQUYiLz4KPC9zdmc+';
                          }}
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 transition-opacity rounded-lg flex items-center justify-center">
                          <a
                            href={fullMechanic.documents.profilePhoto.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="opacity-0 hover:opacity-100 transition-opacity bg-white bg-opacity-90 rounded-full p-2"
                          >
                            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                          </a>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Uploaded: {formatDateString(fullMechanic.documents.profilePhoto.uploadedAt)}
                      </p>
                    </div>
                  )}

                  {/* Business ID Card */}
                  {fullMechanic.documents.businessIdCard && (
                    <div className="text-center">
                      <h3 className="text-sm font-medium text-gray-500 mb-3">Business ID Card</h3>
                      <div className="relative">
                        <img
                          src={fullMechanic.documents.businessIdCard.url}
                          alt="Business ID"
                          className="w-full h-32 object-cover rounded-lg border border-gray-200"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTI4IiBoZWlnaHQ9IjEyOCIgdmlld0JveD0iMCAwIDEyOCAxMjgiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMjgiIGhlaWdodD0iMTI4IiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik02NCA0MEM3My45NDExIDQwIDgyIDQ4LjA1ODkgODIgNThDODIgNjcuOTQxMSA3My45NDExIDc2IDY0IDc2QzU0LjA1ODkgNzYgNDYgNjcuOTQxMSA0NiA1OEM0NiA0OC4wNTg5IDU0LjA1ODkgNDAgNjQgNDBaIiBmaWxsPSIjOUNBM0FGIi8+CjxwYXRoIGQ9Ik0zNiA5NkMzNiA4NS4wNTg5IDQ0LjA1ODkgNzcgNTUgNzdINzNDODMuOTQxMSA3NyA5MiA4NS4wNTg5IDkyIDk2VjEwNEgzNlY5NloiIGZpbGw9IiM5Q0EzQUYiLz4KPC9zdmc+';
                          }}
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 transition-opacity rounded-lg flex items-center justify-center">
                          <a
                            href={fullMechanic.documents.businessIdCard.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="opacity-0 hover:opacity-100 transition-opacity bg-white bg-opacity-90 rounded-full p-2"
                          >
                            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                          </a>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Uploaded: {formatDateString(fullMechanic.documents.businessIdCard.uploadedAt)}
                      </p>
                    </div>
                  )}

                  {/* State ID Card Front */}
                  {fullMechanic.documents.stateIdCardFront && (
                    <div className="text-center">
                      <h3 className="text-sm font-medium text-gray-500 mb-3">State ID Card (Front)</h3>
                      <div className="relative">
                        <img
                          src={fullMechanic.documents.stateIdCardFront.url}
                          alt="State ID Front"
                          className="w-full h-32 object-cover rounded-lg border border-gray-200"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTI4IiBoZWlnaHQ9IjEyOCIgdmlld0JveD0iMCAwIDEyOCAxMjgiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMjgiIGhlaWdodD0iMTI4IiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik02NCA0MEM3My45NDExIDQwIDgyIDQ4LjA1ODkgODIgNThDODIgNjcuOTQxMSA3My45NDExIDc2IDY0IDc2QzU0LjA1ODkgNzYgNDYgNjcuOTQxMSA0NiA1OEM0NiA0OC4wNTg5IDU0LjA1ODkgNDAgNjQgNDBaIiBmaWxsPSIjOUNBM0FGIi8+CjxwYXRoIGQ9Ik0zNiA5NkMzNiA4NS4wNTg5IDQ0LjA1ODkgNzcgNTUgNzdINzNDODMuOTQxMSA3NyA5MiA4NS4wNTg5IDkyIDk2VjEwNEgzNlY5NloiIGZpbGw9IiM5Q0EzQUYiLz4KPC9zdmc+';
                          }}
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 transition-opacity rounded-lg flex items-center justify-center">
                          <a
                            href={fullMechanic.documents.stateIdCardFront.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="opacity-0 hover:opacity-100 transition-opacity bg-white bg-opacity-90 rounded-full p-2"
                          >
                            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                          </a>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Uploaded: {formatDateString(fullMechanic.documents.stateIdCardFront.uploadedAt)}
                      </p>
                    </div>
                  )}

                  {/* State ID Card Back */}
                  {fullMechanic.documents.stateIdCardBack && (
                    <div className="text-center">
                      <h3 className="text-sm font-medium text-gray-500 mb-3">State ID Card (Back)</h3>
                      <div className="relative">
                        <img
                          src={fullMechanic.documents.stateIdCardBack.url}
                          alt="State ID Back"
                          className="w-full h-32 object-cover rounded-lg border border-gray-200"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTI4IiBoZWlnaHQ9IjEyOCIgdmlld0JveD0iMCAwIDEyOCAxMjgiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMjgiIGhlaWdodD0iMTI4IiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik02NCA0MEM3My45NDExIDQwIDgyIDQ4LjA1ODkgODIgNThDODIgNjcuOTQxMSA3My45NDExIDc2IDY0IDc2QzU0LjA1ODkgNzYgNDYgNjcuOTQxMSA0NiA1OEM0NiA0OC4wNTg5IDU0LjA1ODkgNDAgNjQgNDBaIiBmaWxsPSIjOUNBM0FGIi8+CjxwYXRoIGQ9Ik0zNiA5NkMzNiA4NS4wNTg5IDQ0LjA1ODkgNzcgNTUgNzdINzNDODMuOTQxMSA3NyA5MiA4NS4wNTg5IDkyIDk2VjEwNEgzNlY5NloiIGZpbGw9IiM5Q0EzQUYiLz4KPC9zdmc+';
                          }}
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 transition-opacity rounded-lg flex items-center justify-center">
                          <a
                            href={fullMechanic.documents.stateIdCardBack.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="opacity-0 hover:opacity-100 transition-opacity bg-white bg-opacity-90 rounded-full p-2"
                          >
                            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                          </a>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Uploaded: {formatDateString(fullMechanic.documents.stateIdCardBack.uploadedAt)}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No documents uploaded</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    This service provider has not uploaded any documents yet.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Device Details Card */}
          <div className="bg-white rounded-lg shadow border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-medium text-gray-900 flex items-center">
                <div className="p-2 bg-indigo-100 rounded-lg mr-3">
                  <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
                Device Details
              </h2>
              {(() => {
                const deviceInfo = mechanic.deviceInfo;
                const fcmToken = deviceInfo?.fcmToken || mechanic.fcmToken;
                return fcmToken ? (
                  <button
                    onClick={handleTestNotification}
                    disabled={isSendingNotification}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSendingNotification ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Sending...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                        </svg>
                        Test Notification
                      </>
                    )}
                  </button>
                ) : null;
              })()}
            </div>
            <div className="px-6 py-4">
              {(() => {
                // Check if deviceInfo object exists
                const deviceInfo = fullMechanic.deviceInfo;
                
                // Fallback to individual fields if deviceInfo object doesn't exist
                const hasLegacyFields = fullMechanic.fcmToken || fullMechanic.deviceType || fullMechanic.deviceId || fullMechanic.deviceModel || fullMechanic.osVersion;
                
                // Use deviceInfo if available, otherwise fall back to legacy fields
                const device = deviceInfo || (hasLegacyFields ? {
                  fcmToken: fullMechanic.fcmToken,
                  deviceType: fullMechanic.deviceType,
                  deviceId: fullMechanic.deviceId,
                  deviceModel: fullMechanic.deviceModel,
                  osVersion: fullMechanic.osVersion
                } : null);
                
                if (device) {
                  return (
                    <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Device Type</dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {device.deviceType ? (
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              device.deviceType === 'ios' 
                                ? 'bg-blue-100 text-blue-800 border border-blue-200' 
                                : 'bg-green-100 text-green-800 border border-green-200'
                            }`}>
                              {device.deviceType === 'ios' ? (
                                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" />
                                </svg>
                              ) : (
                                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                                </svg>
                              )}
                              {device.deviceType.toUpperCase()}
                            </span>
                          ) : (
                            'Not available'
                          )}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Device Model</dt>
                        <dd className="mt-1 text-sm text-gray-900">{device.deviceModel || 'Not available'}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">OS Version</dt>
                        <dd className="mt-1 text-sm text-gray-900">{device.osVersion || 'Not available'}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Device ID</dt>
                        <dd className="mt-1 text-sm text-gray-900 font-mono break-all">
                          {device.deviceId || 'Not available'}
                        </dd>
                      </div>
                      <div className="sm:col-span-2">
                        <dt className="text-sm font-medium text-gray-500">FCM Token</dt>
                        <dd className="mt-1 text-sm text-gray-900 font-mono break-all bg-gray-50 p-2 rounded border border-gray-200">
                          {device.fcmToken ? (
                            <span className="text-xs">{device.fcmToken}</span>
                          ) : (
                            'Not available'
                          )}
                        </dd>
                      </div>
                    </dl>
                  );
                } else {
                  return (
                    <div className="text-center py-8">
                      <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                      <h3 className="mt-2 text-sm font-medium text-gray-900">No device details available</h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Device information has not been registered for this service provider.
                      </p>
                    </div>
                  );
                }
              })()}
            </div>
          </div>
          </div>
      </div>

      {/* Status Update Modal */}
      <StatusUpdateModal
        isOpen={statusModalOpen}
        onClose={handleCloseStatusModal}
        serviceProvider={fullMechanic}
        onStatusUpdate={handleStatusUpdate}
        isUpdating={isUpdatingStatus}
      />
    </div>
  );
};
